import React, { useEffect, useState } from 'react';
import { cn } from '../../../lib/utils';
import type { ToolRegistry, ToolUiLocation } from '../../../core/tools/ToolRegistry';

const CHROME_MOUNTS: ToolUiLocation[] = ['selectionAction', 'canvasCreate', 'canvasMode'];

const isInspectorEnabled = (): boolean => {
  if (!import.meta.env.DEV) return false;
  try {
    return window.localStorage.getItem('creately:chrome-inspector') === '1';
  } catch {
    return false;
  }
};

interface ChromeInspectorProps {
  toolRegistry?: ToolRegistry;
}

/**
 * Dev-only exclusion-reason panel for canvas chrome mounts (plan §6.5).
 * Enable with localStorage['creately:chrome-inspector'] = '1'.
 * Answers "why is this tool not showing" without guesswork.
 */
export const ChromeInspector: React.FC<ChromeInspectorProps> = ({ toolRegistry }) => {
  const [version, setVersion] = useState(0);
  const enabled = isInspectorEnabled();

  useEffect(() => {
    if (!enabled || !toolRegistry) return;
    const eventBus = toolRegistry.getContext().eventBus;
    const bump = () => setVersion((v) => v + 1);
    const offContextChanged = eventBus.on('tool:contextChanged', bump);
    const offRegistered = eventBus.on('tool:registered', bump);
    const offUnregistered = eventBus.on('tool:unregistered', bump);
    return () => {
      offContextChanged?.();
      offRegistered?.();
      offUnregistered?.();
    };
  }, [enabled, toolRegistry]);

  if (!enabled || !toolRegistry) return null;

  void version;
  const context = toolRegistry.getContext();
  const summary = context.selectionSummary;

  return (
    <div
      data-testid="tid.editor.chromeInspector"
      className={cn(
        'fixed bottom-24 left-4 z-[100] max-h-[50vh] w-[380px] overflow-auto rounded-lg border',
        'bg-background/95 p-3 font-mono text-[11px] shadow-xl backdrop-blur'
      )}
    >
      <div className="mb-2 font-semibold">
        Chrome inspector · contextType={summary?.contextType ?? 'none'} · diagram={context.diagramType} · ownership=
        {context.presentationOwnership ?? 'user'}
      </div>
      {CHROME_MOUNTS.map((mount) => {
        const rows = toolRegistry
          .explainToolsForLocation(mount, summary)
          .filter((row) => row.visible || row.tool.manifest.ui[mount] === true);
        return (
          <div key={mount} className="mb-2">
            <div className="font-semibold text-muted-foreground">{mount}</div>
            {rows.length === 0 && <div className="pl-2 text-muted-foreground">— no manifests for this mount</div>}
            {rows.map(({ tool, visible, excludedBy }) => (
              <div key={tool.manifest.id} className="pl-2">
                {visible ? '✓' : '✗'} {tool.manifest.id}
                {!visible && excludedBy ? <span className="text-muted-foreground"> — {excludedBy}</span> : null}
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
};
